// Decides what the Payroll report's filter bar does with a change, and translates between the
// searched filters and the URL.
//
// The report costs three to six seconds to produce behind a blocking modal, so Custom Range mode
// refreshes on an explicit Search press and nothing else, while Pay Frequency mode keeps refreshing
// on every change as it always has. Both modes commit a snapshot of what they searched with, and
// the rows, the printed criteria, the Excel export and the URL all read that snapshot rather than
// the live inputs -- otherwise editing a filter without pressing Search would silently relabel a
// report that has not been re-run.
//
// Two controls deliberately never reach effectOfFilterChange. Show Details toggles which columns
// render rather than which rows come back, so it stays live in both modes and never describes a
// search. The Custom Range date fields have no trigger to consult: the Search button is the only
// one, and it calls the search entry point directly.

import { parseQueryId, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'
import { ALL_BUSINESS_ENTITIES } from '@/lib/constants/businessEntities'
import { getAppliedPayrollFiltersModel } from '@/store/modules/models/payrollReportFilters'
import { validateCustomDateRange } from './customDateRangeValidation'

export const PAYROLL_SEARCH_MODE = {
  PAY_FREQUENCY: 'payFrequency',
  CUSTOM_RANGE: 'customRange'
}

export const PAYROLL_FILTER = {
  PERIOD: 'period',
  BUSINESS_ENTITY: 'businessEntity',
  CLIENT: 'client'
}

export const FILTER_CHANGE_EFFECT = {
  SEARCH: 'search',
  APPLY: 'apply',
  NONE: 'none'
}

export const PAYROLL_REPORT_QUERY_KEYS = [
  'payFrequencyId',
  'dateBegin',
  'dateEnd',
  'businessEntityId',
  'showDetails',
  'clientId',
  'useCustomRange'
]

const { SEARCH, APPLY, NONE } = FILTER_CHANGE_EFFECT

const FILTER_CHANGE_EFFECTS = {
  [PAYROLL_SEARCH_MODE.PAY_FREQUENCY]: {
    [PAYROLL_FILTER.PERIOD]: SEARCH,
    [PAYROLL_FILTER.BUSINESS_ENTITY]: SEARCH,
    [PAYROLL_FILTER.CLIENT]: APPLY
  },
  // Stated rather than left to the fallback below: waiting for Search is the decision this whole
  // mode exists to make, not an absence of one.
  [PAYROLL_SEARCH_MODE.CUSTOM_RANGE]: {
    [PAYROLL_FILTER.BUSINESS_ENTITY]: NONE,
    [PAYROLL_FILTER.CLIENT]: NONE
  }
}

/**
 * What changing one filter should do, in the mode the filter bar is currently in.
 *
 * SEARCH re-runs the report, APPLY commits the change to the searched set without issuing a
 * request, and NONE leaves both alone. Custom Range mode answers NONE for everything: its only
 * trigger is the Search button, which calls the search entry point directly.
 *
 * @param {string} mode - a PAYROLL_SEARCH_MODE value
 * @param {string} filter - a PAYROLL_FILTER value
 * @returns {string} a FILTER_CHANGE_EFFECT value
 */
export function effectOfFilterChange (mode, filter) {
  const effects = FILTER_CHANGE_EFFECTS[mode]

  return (effects && effects[filter]) || NONE
}

/**
 * Whether the filters currently in the inputs may be searched, and why not when they may not.
 *
 * A half-finished range reports invalid with no message: the admin is mid-edit, not mistaken.
 *
 * @param {object} pending - the filters bound to the inputs
 * @returns {{ valid: boolean, error: string|null }}
 */
export function validatePendingSearch ({ payFrequencyId, dateBegin, dateEnd } = {}) {
  if (!payFrequencyId) {
    return { valid: false, error: null }
  }

  return validateCustomDateRange({ dateBegin, dateEnd })
}

/**
 * Snapshots the filters a search is being run with.
 *
 * The applied set's shape is the store's, so anything outside it (Show Details) is dropped here
 * rather than riding along and pretending to describe the search.
 *
 * @param {object} pending - the filters bound to the inputs
 * @returns {object} the applied set
 */
export function buildAppliedFilters (pending = {}) {
  const applied = getAppliedPayrollFiltersModel()

  Object.keys(applied).forEach(key => {
    if (pending[key] !== undefined) {
      applied[key] = pending[key]
    }
  })

  return applied
}

// The absence of a business entity filter is the all-entities sentinel rather than null, so an
// unreadable or missing parameter falls back to it rather than to "no entity at all".
function parseBusinessEntityId (value) {
  if (Number(value) === ALL_BUSINESS_ENTITIES) {
    return ALL_BUSINESS_ENTITIES
  }

  return parseQueryId(value) || ALL_BUSINESS_ENTITIES
}

function parseQueryString (value) {
  return typeof value === 'string' && value.length > 0 ? value : null
}

// null means the query says nothing, which is not the same as saying false: the caller keeps
// whatever the store already holds rather than forcing the switch off.
function parseSwitch (value) {
  return typeof value === 'string' ? value === 'true' : null
}

/**
 * Reads the Payroll report's filters out of a route query.
 *
 * @param {object} query - Vue Router route.query
 * @returns {object} the filters, with showDetails and useCustomRange null where unstated
 */
export function parsePayrollReportQuery (query = {}) {
  const source = query || {}
  const dateBegin = parseQueryString(source.dateBegin)
  const dateEnd = parseQueryString(source.dateEnd)
  const useCustomRange = parseSwitch(source.useCustomRange)

  return {
    payFrequencyId: parseQueryId(source.payFrequencyId),
    dateBegin,
    dateEnd,
    businessEntityId: parseBusinessEntityId(source.businessEntityId),
    clientId: parseQueryId(source.clientId),
    showDetails: parseSwitch(source.showDetails),
    // A query carrying a range but no switch predates the switch, so leave the mode alone and let
    // the dates ride through. A query with no range at all is a bare navigation to the report and
    // must land on Pay Frequency mode.
    useCustomRange: useCustomRange !== null || dateBegin || dateEnd ? useCustomRange : false
  }
}

/**
 * Builds the next route query describing the search on screen.
 *
 * Query params this module does not own are carried through untouched.
 *
 * @param {object} currentQuery - Vue Router route.query, not mutated
 * @param {object} filters - the applied filters plus the live showDetails
 * @returns {object}
 */
export function buildPayrollReportQuery (currentQuery, filters = {}) {
  const { payFrequencyId, dateBegin, dateEnd, businessEntityId, clientId, showDetails, useCustomRange } = filters
  const nextQuery = { ...currentQuery }

  setOrDeleteQueryParam(nextQuery, 'payFrequencyId', payFrequencyId)
  setOrDeleteQueryParam(nextQuery, 'dateBegin', dateBegin)
  setOrDeleteQueryParam(nextQuery, 'dateEnd', dateEnd)
  setOrDeleteQueryParam(nextQuery, 'businessEntityId', businessEntityId === ALL_BUSINESS_ENTITIES ? null : businessEntityId)
  setOrDeleteQueryParam(nextQuery, 'clientId', clientId)
  // Both switches are written even when off, so that an absent param keeps meaning unstated.
  nextQuery.showDetails = showDetails ? 'true' : 'false'
  nextQuery.useCustomRange = useCustomRange ? 'true' : 'false'

  return nextQuery
}

/**
 * True when the query already says exactly what these filters say.
 *
 * This is how the report tells its own URL rewrite apart from browser navigation or a pasted link.
 *
 * @param {object} query - Vue Router route.query
 * @param {object} filters - the applied filters plus the live showDetails
 * @returns {boolean}
 */
export function queryDescribesFilters (query, filters) {
  return !hasQueryChanged(query, buildPayrollReportQuery(query, filters), PAYROLL_REPORT_QUERY_KEYS)
}
